import { prop, indexBy as rIndexBy, values } from 'ramda';

import ooeConstants from '../constants';
import theme from '../theme';

export const debounce = <T extends unknown[]>(fn: (...args: T) => void, time: number) => {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: T) => {
    const functionCall = () => fn(...args);
    clearTimeout(timeout);
    timeout = setTimeout(functionCall, time);
  };
};

export const camelToTitleCase = (text: string) => {
  const result = text.replace(/([A-Z])/g, ' $1');
  return upperCaseFirstLetter(result);
};

export const camelToKebab = (str: string) =>
  str.replace(/[A-Z]+(?![a-z])|[A-Z]/g, ($, ofs) => (ofs ? '-' : '') + $.toLowerCase());

export const upperCaseFirstLetter = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const maskEmail = (email?: string) => {
  if (typeof email !== 'string') {
    return undefined;
  }

  const mask = '*****';
  try {
    const idx = email.indexOf('@');
    if (idx < 0) {
      return mask;
    }
    if (idx < 4) {
      return `${mask}${email.substr(idx)}`;
    }
    return `${email.substr(0, 1)}${mask}${email.substr(idx - 1, 1)}${email.substr(idx)}`;
  } catch (e) {
    return undefined;
  }
};

export const stripNonPOSSpecialCharacters = (value?: string) =>
  value && value.replace(ooeConstants.POS_INVALID_CHARS_REGEX, '');

export const roundNumber = (num: number, decimalPlaces = 2) =>
  +(Math.round(Number(num + `e+${[decimalPlaces]}`)) + `e-${decimalPlaces}`);

export const specialInstrHasPaperGoods = (specialInstructions = '') =>
  ooeConstants.PAPER_GOODS_MESSAGES.some((option) => specialInstructions?.includes(option));

export const stripPaperGoodsFromSpecialInstructions = (specialInstructions: string): string => {
  if (!specialInstrHasPaperGoods(specialInstructions)) {
    return specialInstructions;
  }
  return specialInstructions.replace(/^Please (?:do not include|include)[^.]+\.\s*/i, '').trim();
};

export const isObjWithEmptyStrings = (obj = {}) => values(obj).every((value) => value === '');

export const indexBy = <T extends Record<string, unknown>>(
  items: T[],
  propertyName: string,
  { first = false } = {},
) => {
  const objectOfIndices: Record<string, T> = {};
  items.forEach((item) => {
    const index = item[propertyName] as string;
    if (!first || !objectOfIndices[index]) {
      objectOfIndices[index] = item;
    }
  });
  return objectOfIndices;
};

export const indexByTag = <T extends { tag: string }>(list: T[]) => {
  return rIndexBy(prop('tag'), list);
};

export const isSmallScreen = () => {
  if (typeof window !== 'undefined') {
    return window.innerWidth < +theme.phone.replace('px', '');
  }
  return false;
};
